const DEFAULT_CHART_BACKGROUND = "#32374b"
const MIN_CONTRAST_RATIO = 3
const MIN_COLOR_DISTANCE = 90
const GOLDEN_ANGLE = 137.508

const normalizeHex = (hex) => {
    const value = String(hex || "").replace("#", "")
    if (/^[0-9a-f]{3}$/i.test(value)) {
        return `#${value.split("").map((char) => char + char).join("")}`
    }
    return /^[0-9a-f]{6}$/i.test(value) ? `#${value}` : DEFAULT_CHART_BACKGROUND
}

const hexToRgb = (hex) => {
    const value = normalizeHex(hex).slice(1)
    return {
        r: parseInt(value.slice(0, 2), 16),
        g: parseInt(value.slice(2, 4), 16),
        b: parseInt(value.slice(4, 6), 16),
    }
}

const hslToHex = (hue, saturation, lightness) => {
    const saturationRatio = saturation / 100
    const lightnessRatio = lightness / 100
    const chroma = (1 - Math.abs(2 * lightnessRatio - 1)) * saturationRatio
    const hueSegment = hue / 60
    const secondary = chroma * (1 - Math.abs((hueSegment % 2) - 1))
    const match = lightnessRatio - chroma / 2
    let rgb = [0, 0, 0]

    if (hueSegment < 1) rgb = [chroma, secondary, 0]
    else if (hueSegment < 2) rgb = [secondary, chroma, 0]
    else if (hueSegment < 3) rgb = [0, chroma, secondary]
    else if (hueSegment < 4) rgb = [0, secondary, chroma]
    else if (hueSegment < 5) rgb = [secondary, 0, chroma]
    else rgb = [chroma, 0, secondary]

    return `#${rgb.map((channel) => Math.round((channel + match) * 255)
        .toString(16)
        .padStart(2, "0")).join("")}`
}

const relativeLuminance = (hex) => {
    const {r, g, b} = hexToRgb(hex)
    const channels = [r, g, b].map((channel) => {
        const value = channel / 255
        return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
    })
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
}

const getContrastRatio = (firstColor, secondColor) => {
    const first = relativeLuminance(firstColor)
    const second = relativeLuminance(secondColor)
    return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05)
}

const getColorDistance = (firstColor, secondColor) => {
    const first = hexToRgb(firstColor)
    const second = hexToRgb(secondColor)
    return Math.sqrt(
        (first.r - second.r) ** 2 +
        (first.g - second.g) ** 2 +
        (first.b - second.b) ** 2
    )
}

const hashString = (value) => Array.from(String(value || "")).reduce(
    (hash, char) => ((hash << 5) - hash + char.charCodeAt(0)) | 0,
    0
)

export const getContrastSafeRandomColor = (
    backgroundHex = DEFAULT_CHART_BACKGROUND,
    usedColors = [],
    seed
) => {
    const background = normalizeHex(backgroundHex)
    const normalizedUsedColors = usedColors.map(normalizeHex)
    const initialHue = seed === undefined
        ? Math.random() * 360
        : Math.abs(hashString(seed)) % 360
    let bestCandidate = "#ffffff"
    let bestScore = -Infinity

    for (let index = 0; index < 48; index += 1) {
        const hue = (initialHue + index * GOLDEN_ANGLE) % 360
        const saturation = 68 + (index % 3) * 7
        const lightness = 64 + (index % 4) * 5
        const candidate = hslToHex(hue, saturation, lightness)
        const contrast = getContrastRatio(candidate, background)
        const distance = normalizedUsedColors.length
            ? Math.min(...normalizedUsedColors.map((color) => getColorDistance(candidate, color)))
            : 255
        const score = distance + contrast * 18

        if (score > bestScore) {
            bestCandidate = candidate
            bestScore = score
        }

        if (contrast >= MIN_CONTRAST_RATIO && distance >= MIN_COLOR_DISTANCE) {
            return candidate
        }
    }

    return bestCandidate
}

export const generateChartColor = (label, usedColors = [], backgroundHex) => (
    getContrastSafeRandomColor(backgroundHex, usedColors, label)
)
